const features = [
  {
    icon: 'ri-database-2-line',
    title: 'Product Organization',
    description: 'Organize hundreds of products in a structured database instead of scattered notes. Keep everything searchable and accessible in one spreadsheet system.',
  },
  {
    icon: 'ri-scales-3-line',
    title: 'Price Comparison',
    description: 'Compare suppliers and prices side by side with built-in comparison views. Spot better deals instantly and make data-driven purchasing decisions.',
  },
  {
    icon: 'ri-time-line',
    title: 'Research Efficiency',
    description: 'Speed up your product research workflow dramatically. Find, filter, and evaluate products faster than traditional manual browsing methods.',
  },
  {
    icon: 'ri-flow-chart',
    title: 'Shopping Workflow',
    description: 'Streamline your entire purchasing process from discovery to delivery. Track status, manage orders, and never lose sight of any item.',
  },
  {
    icon: 'ri-shield-check-line',
    title: 'Data Security',
    description: 'Keep your shopping data organized and protected. Avoid scattered information across different platforms and maintain full control over your records.',
  },
  {
    icon: 'ri-bar-chart-grouped-line',
    title: 'Smart Analytics',
    description: 'Gain insights into your shopping patterns with structured data. Identify trends, optimize spending, and make smarter long-term purchasing plans.',
  },
];

export default function FeatureSection() {
  return (
    <section className="py-14 md:py-20 bg-background-100">
      <div className="w-full max-w-[1440px] mx-auto px-4 md:px-8">
        {/* Header */}
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between mb-10 md:mb-14">
          <div className="max-w-xl">
            <span className="inline-block px-3 py-1 rounded-full bg-primary-100 text-primary-700 text-xs font-medium mb-3">
              Why Use It
            </span>
            <h2 className="font-heading text-2xl md:text-4xl font-semibold text-foreground-950">
              Why Thousands of Users Prefer Superbuy Spreadsheet
            </h2>
          </div>
          <p className="mt-3 lg:mt-0 text-sm text-foreground-500 max-w-sm">
            A structured spreadsheet system transforms chaotic shopping into an organized, efficient workflow
          </p>
        </div>

        {/* Comparison Table */}
        <div className="mb-12 overflow-x-auto">
          <table className="w-full border-collapse">
            <thead>
              <tr className="border-b border-background-200/70">
                <th className="text-left py-3 px-4 text-sm font-semibold text-foreground-700">Feature</th>
                <th className="text-left py-3 px-4 text-sm font-semibold text-foreground-400">Without Superbuy Spreadsheet</th>
                <th className="text-left py-3 px-4 text-sm font-semibold text-accent-700">With Superbuy Spreadsheet</th>
              </tr>
            </thead>
            <tbody>
              {[
                { feature: 'Product Organization', without: 'Scattered manual notes', with: 'Structured searchable database' },
                { feature: 'Price Tracking', without: 'Difficult to compare', with: 'Easy side-by-side comparison' },
                { feature: 'Product Research', without: 'Slow manual browsing', with: 'Fast filtered evaluation' },
                { feature: 'Shopping Management', without: 'Complex and fragmented', with: 'Organized in one place' },
                { feature: 'Decision Making', without: 'Limited by scattered data', with: 'Data-driven and informed' },
              ].map((row) => (
                <tr key={row.feature} className="border-b border-background-200/40 hover:bg-background-50/60 transition-colors">
                  <td className="py-3 px-4 text-sm font-medium text-foreground-800">{row.feature}</td>
                  <td className="py-3 px-4 text-sm text-foreground-500">{row.without}</td>
                  <td className="py-3 px-4 text-sm text-accent-700 font-medium">{row.with}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Feature Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
          {features.map((feature) => (
            <div
              key={feature.title}
              className="bg-background-50 rounded-2xl p-5 md:p-6 border border-background-200/50 hover:border-primary-200 transition-all duration-300"
            >
              <div className="w-10 h-10 md:w-11 md:h-11 flex items-center justify-center rounded-xl bg-accent-100 text-accent-600 mb-4">
                <i className={`${feature.icon} text-lg md:text-xl`}></i>
              </div>
              <h3 className="font-semibold text-sm md:text-base text-foreground-950 mb-2">
                {feature.title}
              </h3>
              <p className="text-xs md:text-sm text-foreground-500 leading-relaxed">
                {feature.description}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}